<template>
  <div class="c-tooltips">
    <template v-if="isEditingSportWithTooltips">
      <button
        type="button"
        class="ui-btn ui-btn--ghost c-tooltips__back"
        @click="toggleEditingMode"
      >
        <app-icon name="arrow_back" />
        All sports
      </button>

      <sport-tooltips
        :website="website"
        :sport="selectedSport"
        :go-back="toggleEditingMode"
        :languages="allOnlineLanguages"
        :tooltip-section="tooltipSection"
      />
    </template>

    <template v-else>
      <div class="c-tooltips__header">
        <div>
          <h3 class="c-tooltips__title">
            Sport tooltips
          </h3>
          <p class="c-tooltips__subtitle">
            Tooltips are grouped by sport. Open a sport to manage its tooltips.
          </p>
        </div>

        <div
          v-if="canWrite(permissionName)"
          class="c-tooltips__actions"
        >
          <label
            class="ui-btn ui-btn--secondary"
            for="tooltips-import-file"
            title="Import tooltips from a CSV file"
          >
            <app-icon name="upload_file" />
            Import tooltips
          </label>
          <input
            id="tooltips-import-file"
            ref="file"
            class="c-tooltips__file"
            accept=".csv"
            type="file"
            @change="handleOnFileChange($event)"
          >
          <button
            type="button"
            class="ui-btn ui-btn--secondary"
            title="Import tooltip translations from a CSV file"
            @click="openImportTranslationsModal()"
          >
            <app-icon name="translate" />
            Import translations
          </button>
        </div>
      </div>

      <div class="c-tooltips__toolbar">
        <div class="ui-field c-tooltips__search">
          <div class="ui-field__control ui-field__control--with-action">
            <app-icon
              class="ui-field__icon"
              name="search"
            />
            <input
              v-model="searchQuery"
              class="ui-field__input"
              type="text"
              placeholder="Search sports by name or ID…"
              aria-label="Search sports"
              @input="handleSearch"
            >
            <button
              v-if="searchQuery"
              type="button"
              class="ui-field__action"
              title="Clear search"
              aria-label="Clear search"
              @click="searchQuery = ''; handleSearch()"
            >
              <app-icon name="close" />
            </button>
          </div>
        </div>

        <div
          v-if="canWrite(permissionName)"
          class="c-tooltips__add"
        >
          <md-field class="c-tooltips__add-select">
            <md-select
              id="sport"
              v-model="selectedSportId"
              name="sport"
              placeholder="Choose a sport to add…"
              aria-label="Sport to add"
              md-dense
            >
              <md-option
                v-for="sport in sportsWithoutTooltipsDropdownData"
                :key="sport.sportId"
                :value="sport.sportId"
              >
                {{ sport.name }}
              </md-option>
            </md-select>
          </md-field>
          <button
            type="button"
            class="ui-btn ui-btn--primary"
            :disabled="!selectedSportId"
            @click="createSportWithTooltips(selectedSportId)"
          >
            <app-icon name="add" />
            Add sport
          </button>
        </div>
      </div>

      <div class="c-table-wrapper c-tooltips__table">
        <base-table-component
          :prop-data="sportsWithTooltipsTableData"
          :empty-title="searchQuery ? 'No sports found' : 'No sport tooltips yet'"
          :empty-description="searchQuery ? `Nothing matches “${searchQuery}”.` : 'Choose a sport above and add it to start creating tooltips.'"
        >
          <template v-slot:default="slotProps">
            <span class="c-table__btn-wrap">
              <generic-button
                v-if="canWrite(permissionName)"
                icon
                transparent
                text-inherit
                @click="openDeleteModal(slotProps.selected)"
              >
                <md-tooltip md-direction="top">
                  Delete sport tooltips
                </md-tooltip>
                <img src="../../../../../assets/delete-button.svg" alt="">
              </generic-button>
            </span>
            <button
              type="button"
              class="ui-btn ui-btn--secondary ui-btn--sm c-tooltips__open"
              @click="openEditSportWithTooltipsTable(slotProps.selected)"
            >
              Open
              <app-icon
                name="chevron_right"
                :size="18"
              />
            </button>
          </template>
        </base-table-component>
      </div>

      <paginate
        v-if="totalPages > 1"
        v-model="currentPage"
        :page-count="totalPages"
        :prev-text="'‹'"
        :next-text="'›'"
        :page-range="20"
        :container-class="'c-pagination'"
        :prev-link-class="'md-button prev md-elevation-1'"
        :next-link-class="'md-button next md-elevation-1'"
        :page-link-class="'md-button md-elevation-1'"
        :active-class="'active'"
        :disabled-class="'disabled'"
      />
    </template>
  </div>
</template>

<script>
  import { tooltipsService } from '@/services/tooltips';
  import GenericButton from '@/shared/buttons/GenericButton';
  import BaseTableComponent from '@/shared/table/BaseTableComponent';
  import { mapActions } from 'vuex';
  import DeleteModalComponent from '@/shared/delete-modal/DeleteModalComponent.vue';
  import SportTooltips from './sub-pages/sport-tooltips';
  import { responseService } from '@/services/response';
  import { languageService } from '@/services/language';
  import { sectionService } from '@/services/section';
  import find from 'lodash/find';
  import get from 'lodash/get';
  import filter from 'lodash/filter';
  import ImportTranslationsModal from './import-translations-modal';
  import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
  import {PERMISSION_NAMES} from "@/const/online-permissions";
  import AppIcon from '@/shared/app-icon/AppIcon';

  export default {
    name: 'OnlineTooltipsSportList',

    components: {
      GenericButton,
      BaseTableComponent,
      SportTooltips,
      AppIcon,
    },

    mixins: [onlinePermissionsMixin],

    props: {
      website: {
        type: Object,
        default: null,
      },
    },

    data() {
      return {
        permissionName: `${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.TOOLTIPS.key}`,
        searchQuery: '',

        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,

        fetchedResults: false,
        showAllSportsWithTooltips: false,
        isEditingSportWithTooltips: false,
        sportsWithTooltips: [],
        filteredSportsWithTooltips: [],
        sports: [],
        selectedSport: {},
        selectedSportId: 0,
        allOnlineLanguages: [],
        tooltipSection: {},
      };
    },

    computed: {
      PERMISSION_NAMES() {
        return PERMISSION_NAMES
      },

      sportsWithTooltipsTableData() {
        if (!this.filteredSportsWithTooltips) {
          return [];
        }

        const currentPage = this.currentPage || 1;
        const unfilteredSports = this.filteredSportsWithTooltips.map(sportWithTooltip => ({
          sportWithTooltip: sportWithTooltip,
          rowData: {
            'Sport Id': sportWithTooltip.sportId,
            'Sport': sportWithTooltip?.name,
          }
        }))

        return this.filterSportsWithTooltips(unfilteredSports)
          .slice((currentPage - 1) * this.itemsPerPage, currentPage * this.itemsPerPage);
      },

      sportsWithoutTooltipsDropdownData() {
        if(!this.sports) {
          return [];
        }

        return this.sports.filter((sport) => {
          return this.filteredSportsWithTooltips.filter((sportWithTooltips) => {
              return sportWithTooltips.sportId === sport.sportId;
          }).length === 0
        });
      }
    },

    async created() {
      await this.getSports();
      this.getSportsWithTooltips();
      this.getAvailableLanguages();
      this.getAvailableSections();
    },

    methods: {
      ...mapActions("response", ["handleSuccess", "setGlobalError", "resetError", "enableFullscreenLoader", "disableFullscreenLoader", "handleError"]),

      async getSportsWithTooltips() {
        const sportsWithTooltipsResponse = await tooltipsService.getAllSportsWithTooltips();
        this.sportsWithTooltips = sportsWithTooltipsResponse?.data?.items;
        this.filteredSportsWithTooltips = this.sportsWithTooltips.map(sportWithTooltip => {
          return {
            ...sportWithTooltip,
            name: this.sports.find(sport => sport.sportId === sportWithTooltip.sportId)?.name
          }
        });
        this.fetchedResults = true;

        this.handlePaginationParameters();
      },

      async getSports() {
        if (this.sports.length) {
          return;
        }

        const sportsResponse = await tooltipsService.getSports();
        this.sports = sportsResponse?.data;
      },

      async deleteSportWithTooltips(id) {
        try {
          await tooltipsService.delete(id);
          this.handleSuccess({ message: 'Successfully deleted sport tooltips' })
        } catch (error) {
          this.setGlobalError(responseService.getErrorMessage(error.data.message));
        }
      },

      async createSportWithTooltips(sportId) {
        if (!sportId) {
          return;
        }

        try {
          const createData = {
              "websiteIds": "[]",
              "sportId": sportId,
              "tooltips": "[]"
          }
          const response = await tooltipsService.create(createData);
          this.handleSuccess({ message: 'Successfully created sport'});
          const newSport = find(this.sports, { sportId });
          this.addNewCreatedSportInFilteredSports(newSport, response);
          this.setSelectedSport(newSport, response);
          this.toggleEditingMode();
        } catch (error) {
          this.setGlobalError(responseService.getErrorMessage(error.data.message));
        }
      },

      openDeleteModal(selected) {
        this.$modal.show(
          DeleteModalComponent,
          {
            deleteMsg: selected.rowData.Sport,
            deleteMsgAlert: `It will be deleted on all websites.`,
            propData: selected.sportWithTooltip.sportId,
            deleteFunction: this.deleteSportWithTooltips,
            getDataFunction: this.getSportsWithTooltips,
            pageData: {}
          },
          { height: "auto", scrollable: false, width: "440px" }
        );
      },

      openEditSportWithTooltipsTable(selected) {
        this.selectedSport = selected;
        this.toggleEditingMode();
      },

      toggleEditingMode() {
        this.isEditingSportWithTooltips = !this.isEditingSportWithTooltips;
      },

      openImportTranslationsModal() {
        this.$modal.show(
          ImportTranslationsModal,
          {},
          { height: "auto", scrollable: false, width: "600px" }
        );
      },

      async handleOnFileChange(event) {
        const formData = new FormData();
        formData.set('file', event.target.files[0]);
        this.enableFullscreenLoader();
        try {
          await tooltipsService.uploadTooltips(formData);
          this.handleSuccess({message: `Tooltips imported!`});
          this.getSportsWithTooltips();
        } catch (e) {
          this.setGlobalError(get(e, 'data.message', 'Error with importing!' ));
        } finally {
          this.disableFullscreenLoader();
        }
      },

      handleSearch() {
        const filteredSports = this.filterSportsWithTooltips(this.filteredSportsWithTooltips);
        this.handlePaginationParameters(filteredSports.length)
      },

      handlePaginationParameters(totalItemsCount = null) {
        const tooltipsCount = totalItemsCount !== null ? totalItemsCount : this.sportsWithTooltips.length;

        this.totalPages = Math.ceil(tooltipsCount / this.itemsPerPage)
        this.totalItems = tooltipsCount;

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
        }
      },

      async getAvailableSections() {
        const data = await sectionService.onlineGetAllSections(0, 99999, false);
        this.tooltipSection = find(get(data, 'data.items', []), {key:'tooltips'});
      },

      async getAvailableLanguages() {
        const allLanguagesResponse = await languageService.getAllOnlineLanguages();
        this.allOnlineLanguages = allLanguagesResponse.data.items;
      },

      filterSportsWithTooltips(unfilteredSports) {
        return filter(unfilteredSports, sportWithTooltip => {
          if (sportWithTooltip.rowData) {
            return sportWithTooltip.sportWithTooltip.name.toLowerCase().includes(this.searchQuery.toLowerCase())
              || sportWithTooltip.sportWithTooltip.sportId.toString().includes(this.searchQuery)
          }

          return sportWithTooltip.name.toLowerCase().includes(this.searchQuery.toLowerCase())
            || sportWithTooltip.sportId.toString().includes(this.searchQuery)
        })
      },
      addNewCreatedSportInFilteredSports(newSport, response) {
        this.filteredSportsWithTooltips.push({
            "id": response.data.id,
            "sportId": response.data.sportId,
            "name": get(newSport, 'name', ''),
            "websiteIds": response.data.websiteIds
          });
      },
      setSelectedSport(newSport, response) {
        this.selectedSport = {
            sportWithTooltip: {
              "id": response.data.id,
              "sportId": response.data.sportId,
              "tooltips": response.data.tooltips,
              "websiteIds": response.data.websiteIds
            },
            rowData: {
              'Sport Id': response.data.sportId,
              'Sport': get(newSport, 'name', '')
            }
          }
        this.selectedSportId = 0;
      }
    },
  }
</script>

<style lang="scss" scoped>
.c-tooltips__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.c-tooltips__title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-tooltips__subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.c-tooltips__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.c-tooltips__file {
  display: none;
}

.c-tooltips__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.c-tooltips__search {
  flex: 1 1 260px;
  max-width: 360px;

  .ui-field__input {
    height: 40px;
    font-size: 14px;
  }
}

.c-tooltips__add {
  display: flex;
  align-items: center;
  gap: 8px;
}

.c-tooltips__add-select {
  width: 240px;
  margin: 0;
}

.c-tooltips__table {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.c-tooltips__open {
  margin-left: 8px;
  vertical-align: middle;
}

.c-tooltips__back {
  margin: -4px 0 12px -8px;
}
</style>
